import * as React from 'react';
import i18n from 'i18next';

// components
import LabelGroup from 'components/_UI/Forms/LabelGroup/LabelGroup';
import ActionSelectGroup from 'components/_UI/Forms/Actions/ActionSelectGroup';
import ActionCheckboxGroup from 'components/_UI/Forms/Actions/ActionCheckboxGroup';
import ActionInput from 'components/_UI/Forms/Actions/ActionInput';
import { CollapseProps, TunablesCollapse } from './RenderTunables';
import ActionSliderGroup from 'components/_UI/Forms/Actions/ActionSliderGroup';
import ActionCheckboxSliderGroup from 'components/_UI/Forms/Actions/ActionCheckboxSliderGroup';

// types
import { Widget } from '@sprinklr/stories/widget/Widget';
import { Theme } from 'models/Theme/Theme';

// helpers
import { getOrientationFromPosition } from '@sprinklr/display-builder/widgets/hyperSpace/utils/getOrientationFromPosition';
import { getLegendPosition } from '@sprinklr/display-builder/widgets/hyperSpace/utils/getLegendPosition';
import { widgetDimIsDate } from 'utils/Widget/WidgetUtils';

// constants
import { X_AXIS_OPTIONS } from 'components/Panel/PanelEditorSidebar/PanelDesign/PanelDesignOptions';

export const gridTunables = (widget: Widget) => {
    return (
        !widget.options.includeMetrics && (
            <ActionCheckboxGroup
                key='widget.options.baseXYChartOptions.grid.enabled'
                label='Grid'
                option={widget.options.baseXYChartOptions.grid}
                value='enabled'
            />
        )
    );
};
type AxisTunablesConfig = {
    widget: Widget;
    padding?: boolean;
    allPadding?: boolean;
    ticks?: boolean;
    line?: boolean;
    numTicks?: boolean;
    label?: boolean;
    format?: boolean;
    tickLabelName?: string;
    mergedTheme?: Theme;
    axisIsDim?: boolean;
};

export const yAxisTunables = ({
    widget,
    format = true,
    ticks = true,
    mergedTheme,
    axisIsDim = true,
}: AxisTunablesConfig): TunablesCollapse => {
    const isTimeDim: boolean = axisIsDim && widgetDimIsDate(widget);
    const { yAxis } = widget.options.baseXYChartOptions;

    return {
        props: {
            label: 'Y Axis',
            startOpen: true,
            isEnabled: yAxis.enabled,
            option: yAxis,
            value: 'enabled',
        } as CollapseProps,
        children: [
            ticks && (
                <React.Fragment key='baseXYChartOptions.yAxis.ticks'>
                    <ActionCheckboxGroup
                        key='baseXYChartOptions.yAxis.ticks.show'
                        label={i18n.t('Show Ticks')}
                        option={yAxis.ticks}
                        value='enabled'
                    />
                    <ActionSliderGroup
                        key='baseXYChartOptions.yAxis.ticks.numTicks'
                        label={i18n.t('Number of Ticks')}
                        option={yAxis.ticks}
                        value='numTicks'
                        min={0}
                        max={30}
                    />
                </React.Fragment>
            ),
            yAxis?.ticks?.formats?.numberFormat && format && !isTimeDim && (
                <>
                    <ActionCheckboxGroup
                        key='baseXYChartOptions.yAxis.ticks.formats.numberFormat.useShortened'
                        label={i18n.t('Shortened Number')}
                        option={yAxis.ticks.formats.numberFormat}
                        value='useShortened'
                    />
                    <LabelGroup
                        key='baseXYChartOptions.yAxis.ticks.formats.numberFormat.decimals'
                        label={i18n.t('Decimals')}>
                        <ActionInput
                            min={0}
                            max={10}
                            type='number'
                            option={yAxis.ticks.formats.numberFormat}
                            value='decimals'
                        />
                    </LabelGroup>
                </>
            ),
            isTimeDim && (
                <>
                    <ActionCheckboxGroup
                        key='baseXYChartOptions.xAxis.ticks.formats.dateFormat.autoFormat'
                        label={i18n.t('Auto Date Format')}
                        option={yAxis.ticks.formats.dateFormat}
                        value='autoFormat'
                    />
                </>
            ),
            isTimeDim && !yAxis.ticks.formats.dateFormat.autoFormat && (
                <ActionSelectGroup
                    key='baseXYChartOptions.yAxis.timeFormat'
                    label='Date Format'
                    option={yAxis.ticks.formats.dateFormat}
                    value='formatString'
                    options={X_AXIS_OPTIONS}
                />
            ),

            <ActionCheckboxSliderGroup
                key='baseXYChartOptinos.yAxis.line'
                label={i18n.t('Axis Line')}
                showValue
                checkbox={{
                    option: yAxis.line,
                    value: 'enabled',
                }}
                slider={{
                    option: yAxis.line,
                    value: 'strokeWidth',
                    min: 0,
                    max: 10,
                }}
            />,
            <ActionCheckboxSliderGroup
                key='baseXYChartOptions.yAxis.label'
                label={i18n.t('Axis Label')}
                showValue
                checkbox={{
                    option: yAxis.label,
                    value: 'enabled',
                }}
                slider={{
                    option: yAxis.label,
                    value: 'size',
                    min: 1,
                    max: 100,
                }}
            />,
        ],
    };
};

export const xAxisTunables = ({
    widget,
    format = true,
    ticks = true,
    mergedTheme,
    axisIsDim = true,
}: AxisTunablesConfig): TunablesCollapse => {
    const isTimeDim: boolean = axisIsDim && widgetDimIsDate(widget);
    const { xAxis } = widget.options.baseXYChartOptions;

    return {
        props: {
            label: 'X Axis',
            startOpen: true,
            isEnabled: xAxis.enabled,
            option: xAxis,
            value: 'enabled',
        } as CollapseProps,
        children: [
            ticks && (
                <React.Fragment key='baseXYChartOptions.xAxis.ticks'>
                    <ActionCheckboxGroup
                        key='baseXYChartOptions.xAxis.ticks.show'
                        label={i18n.t('Show Ticks')}
                        option={xAxis.ticks}
                        value='enabled'
                    />
                    <ActionSliderGroup
                        key='baseXYChartOptions.xAxis.ticks.numTicks'
                        label={i18n.t('Number of Ticks')}
                        option={xAxis.ticks}
                        value='numTicks'
                        min={0}
                        max={30}
                    />
                </React.Fragment>
            ),
            xAxis.ticks?.formats?.numberFormat && !isTimeDim && format && (
                <>
                    <ActionCheckboxGroup
                        key='baseXYChartOptions.yAxis.ticks.formats.numberFormat.useShortened'
                        label={i18n.t('Shortened Number')}
                        option={xAxis.ticks.formats.numberFormat}
                        value='useShortened'
                    />
                    <LabelGroup
                        key='baseXYChartOptions.yAxis.ticks.formats.numberFormat.decimals'
                        label={i18n.t('Decimals')}>
                        <ActionInput
                            min={0}
                            max={10}
                            type='number'
                            option={xAxis.ticks.formats.numberFormat}
                            value='decimals'
                        />
                    </LabelGroup>
                </>
            ),
            isTimeDim && (
                <>
                    <ActionCheckboxGroup
                        key='baseXYChartOptions.xAxis.ticks.formats.dateFormat.autoFormat'
                        label={i18n.t('Auto Date Format')}
                        option={xAxis.ticks.formats.dateFormat}
                        value='autoFormat'
                    />
                </>
            ),
            isTimeDim && !xAxis.ticks.formats.dateFormat.autoFormat && (
                <ActionSelectGroup
                    key='baseXYChartOptions.xAxis.timeFormat'
                    label='Date Format'
                    option={xAxis.ticks.formats.dateFormat}
                    value='formatString'
                    options={X_AXIS_OPTIONS}
                />
            ),

            <ActionCheckboxSliderGroup
                key='baseXYChartOptions.xAxis.line'
                label={i18n.t('Axis Line')}
                showValue
                checkbox={{
                    option: xAxis.line,
                    value: 'enabled',
                }}
                slider={{
                    option: xAxis.line,
                    value: 'strokeWidth',
                    min: 0,
                    max: 10,
                }}
            />,
            <ActionCheckboxSliderGroup
                key='baseXYChartOptions.yAxis.label.enabled'
                label={i18n.t('Axis Label')}
                showValue
                checkbox={{
                    option: xAxis.label,
                    value: 'enabled',
                }}
                slider={{
                    option: xAxis.label,
                    value: 'size',
                    min: 1,
                    max: 100,
                }}
            />,
        ],
    };
};

export type LegendTunablesOpts = {
    positionOptions?: ('top' | 'left' | 'bottom' | 'right')[];
    metric?: boolean;
};

export const legendTunables = (widget: Widget, config?: LegendTunablesOpts): TunablesCollapse => {
    const { legend } = widget.options || {};
    const { positionOptions = ['top', 'right', 'bottom', 'left'], metric = true } = config || {};

    const top =
        positionOptions && positionOptions.includes('top')
            ? [{ value: 'top', label: i18n.t('Top') }]
            : [];
    const bottom =
        positionOptions && positionOptions.includes('bottom')
            ? [{ value: 'bottom', label: i18n.t('Bottom') }]
            : [];
    const right =
        positionOptions && positionOptions.includes('right')
            ? [{ value: 'right', label: i18n.t('Right') }]
            : [];
    const left =
        positionOptions && positionOptions.includes('left')
            ? [{ value: 'left', label: i18n.t('Left') }]
            : [];

    return {
        props: {
            label: 'Legend',
            startOpen: true,
            isEnabled: legend.enabled,
            option: legend,
            value: 'enabled',
        } as CollapseProps,
        children: [
            <ActionCheckboxGroup
                key='widget.options.legend.position.auto'
                label={i18n.t('Auto Position')}
                option={widget.options.legend.position}
                value='auto'
            />,
            !widget.options.legend.position.auto && (
                <ActionSelectGroup
                    label={i18n.t('Position')}
                    key='widget.options.legend.position.selectedPosition'
                    option={widget.options.legend.position}
                    value='selectedPosition'
                    options={[...top, ...bottom, ...right, ...left]}
                />
            ),
            getOrientationFromPosition(getLegendPosition(widget.options.legend)) === 'vertical' && (
                <ActionSliderGroup
                    min={100}
                    max={500}
                    showValue
                    key='widget.options.legend.maxWidth'
                    label={i18n.t('Legend Width')}
                    option={widget.options?.legend}
                    value='maxWidth'
                />
            ),
            <ActionSliderGroup
                key='widget.options.legend.size'
                label={i18n.t('Label Size')}
                option={widget.options?.legend}
                value='size'
                showValue
            />,
            metric && (
                <ActionCheckboxGroup
                    key='widget.options.legend.metric.enabled'
                    label={i18n.t('Show Totals')}
                    option={widget.options?.legend?.metric}
                    value='enabled'
                />
            ),
        ],
    };
};

type MetaInfoTunablesConfig = {
    widget: Widget;
    color?: boolean;
    image?: boolean;
};

export const metaInfoTunables = ({
    widget,
    color = true,
    image = true,
}: MetaInfoTunablesConfig): TunablesCollapse => {
    let dimensionName = null;
    let filterName = null;

    const colorBlacklist = ['ADVOCACY', 'BENCHMARKING'];
    const engine = widget.analyticsRequests && widget.analyticsRequests[0].reportingEngine;

    widget.analyticsRequests &&
        widget.analyticsRequests[0]?.filters &&
        widget.analyticsRequests[0].filters.map(filter => {
            const name = filter.dimensionName;
            if (name && name.indexOf('HIERARCHY_ID') === 0) {
                filterName = filter.dimensionName;
            }
        });

    widget.analyticsRequests &&
        widget.analyticsRequests[0].groupBys.map(groupBy => {
            const name = groupBy.dimensionName ? groupBy.dimensionName.toString() : null;

            if (
                name &&
                (name.indexOf('SPECIFIC_THEME_TAG_') === 0 ||
                    name.indexOf('SPECIFIC_TOPIC_TAG_') === 0 ||
                    name.indexOf('SPECIFIC_TOPIC_GROUP_') === 0 ||
                    name === 'TOPIC_IDS' ||
                    name === 'BRAND_ID' ||
                    name === 'COMMUNITY_USER_ID')
            ) {
                dimensionName = groupBy.dimensionName;
            }
        });
    let label: string;
    switch (engine) {
        case 'ADVOCACY':
            label = i18n.t('Community User Attributes');
            break;
        case 'BENCHMARKING':
            label = i18n.t('Benchmarking Attributes');
            break;
        case 'LISTENING':
            label = i18n.t('Topic/Theme Attributes');
            break;
        default:
            if (filterName) {
                label = i18n.t('Insight Attributes');
                break;
            } else {
                label = i18n.t('Topic/Theme Attributes');
                break;
            }
    }
    const metaInfoColor =
        !colorBlacklist.includes(engine) && color ? (
            <ActionCheckboxGroup
                key='widget.options.metaInfoOptions.color'
                label={i18n.t('Color')}
                option={widget.options.metaInfoOptions.color}
                value='enabled'
            />
        ) : null;
    const metaInfoImage = image && (
        <ActionCheckboxGroup
            key='widget.options.metaInfoOptions.image'
            label={i18n.t('Image')}
            option={widget.options.metaInfoOptions.image}
            value='enabled'
        />
    );
    const children = [metaInfoColor, metaInfoImage];
    return {
        props: {
            label: label,
            startOpen: true,
        } as CollapseProps,
        children,
    };
};

export const metricGroupTunables = (widget): TunablesCollapse => {
    const { metricGroupOptions } = widget.options;
    return {
        props: {
            label: 'Metric Group',
            startOpen: true,
        } as CollapseProps,
        children: [
            metricGroupOptions.hasOwnProperty('autoSize') ? (
                <ActionCheckboxGroup
                    key='widget.options.metricGroupOptions.autoSize.enabled'
                    label={i18n.t('Auto Sizing')}
                    option={metricGroupOptions.autoSize}
                    value='enabled'
                />
            ) : null,
            metricGroupOptions?.autoSize?.enabled ? null : (
                <ActionSliderGroup
                    key='widget.options.metricGroupOptions.metric'
                    label={i18n.t('Metric Value')}
                    showValue
                    option={metricGroupOptions.metric}
                    value={'size'}
                    min={0}
                    max={100}
                />
            ),
            <ActionCheckboxGroup
                key='widget.options.metricGroupOptions.metric.format.useShortened'
                label={i18n.t('Shortened Number')}
                option={metricGroupOptions.metric.format}
                value='useShortened'
            />,
            <LabelGroup
                key='widget.options.metricGroupOptions.metric.format.decimals'
                label={i18n.t('Decimals')}>
                <ActionInput
                    min={0}
                    max={10}
                    type='number'
                    option={metricGroupOptions.metric.format}
                    value='decimals'
                />
            </LabelGroup>,
            metricGroupOptions?.autoSize?.enabled ? null : (
                <ActionSliderGroup
                    key='widget.options.metricGroupOptions.metricName'
                    label={i18n.t('Metric Name')}
                    showValue
                    option={metricGroupOptions.metricName}
                    value={'size'}
                    min={0}
                    max={100}
                />
            ),
            !metricGroupOptions.disablePercentChangeOption && (
                <ActionCheckboxSliderGroup
                    key='widget.options.metricGroupOptions.percentChange'
                    label={i18n.t('Percent Change')}
                    showValue
                    checkbox={{
                        option: metricGroupOptions.percentChange,
                        value: 'enabled',
                    }}
                    slider={{
                        option: metricGroupOptions.percentChange,
                        value: 'size',
                        min: 0,
                        max: 100,
                    }}
                />
            ),
        ],
    };
};
